import { isAxiosError } from 'axios';

// What olio-api sends when nothing was passed to Errors::ApiError or Errors::UnprocessableEntityError
// — the class defaults, not a reason anyone wrote for a user. `raise Errors::ApiError, 'Invalid
// "type"'` reads as one of these too: that positional argument sets `error`, leaving `message` the
// default. Callers keep their own wording rather than showing these.
const PLACEHOLDER_MESSAGES = ['Unable to process record', 'Unprocessable Entity'];

/**
 * Reads olio-api's rejection of a write, for callers that want the server's reason on screen.
 *
 * The api refuses a write in two shapes, both at 422. A model validation failure comes back as
 * `{ errors: { field: [reasons] } }`, with OliveBranch camelCasing the field keys. Those reasons are
 * `errors.messages`, which holds fragments written to sit behind their field name ("is already on
 * this list with the same brand name…") rather than whole sentences, so the humanized field goes
 * back in front of each one here. Messages on `base` are already sentences and pass through
 * untouched.
 *
 * An Errors::ApiError renders `{ error, status, message }` with no `errors` key, and its `message`
 * is already a whole sentence meant for a user ("Referral source name is required"), so it is read
 * as-is when there is no field to attribute it to.
 *
 * Returns undefined for anything else — a 500, a dropped connection, a 422 with nothing readable in
 * it — so the caller keeps its own generic wording for the failures where the server says nothing a
 * user could act on. Pair it with useMutateWithToast's `errorToast`, which falls back on undefined.
 *
 * Several screens predate this and read one shape or the other inline (EditClient,
 * PortalAutomationSettings, useSavePortalCredentials, EditUser, ReferralSourceModal). Reach for this
 * when writing a new one or when next in one of those; there is nothing to gain from a sweep.
 */
export function validationErrorMessage(error: unknown): string | undefined {
  if (!isAxiosError(error) || error.response?.status !== 422) return undefined;

  const data = error.response.data;

  const sentences = Object.entries(data?.errors ?? {}).flatMap(([field, reasons]) =>
    (Array.isArray(reasons) ? reasons : [])
      .filter((reason): reason is string => typeof reason === 'string' && reason.length > 0)
      .map((reason) => {
        const sentence = field === 'base' ? reason : `${humanizeField(field)} ${reason}`;

        return sentence.replace(/\.?$/, '.');
      })
  );

  if (sentences.length) return sentences.join(' ');

  const message = data?.message;

  if (typeof message !== 'string' || !message.length || PLACEHOLDER_MESSAGES.includes(message)) return undefined;

  return message.replace(/\.?$/, '.');
}

// genericName -> "Generic name", the way Rails' full_messages reads a field name back to a user.
const humanizeField = (field: string) => {
  const words = field.replace(/([A-Z])/g, ' $1').toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};
